/**
 * Hanro cross-border storefront routing (ECSR-857).
 *
 * Two pieces, both rendered by sections/hanro-country-routing.liquid:
 *
 * 1. <hanro-country-overlay> — a Horizon dialog-component shown on the US and
 *    Canada storefronts to visitors whose location resolves to one of the six
 *    European Hanro storefront countries (GB, DE, CH, FR, IT, ES).
 *
 *    Market detection: those six countries are not Shopify markets on this
 *    store, so Liquid's localization.country is always US or CA for them.
 *    Instead the visitor's country comes from Shopify's own IP geolocation,
 *    `/browsing_context_suggestions.json` (`detected_values.country.handle`),
 *    fetched once per browser session and cached in sessionStorage.
 *
 *    Test hook (safe in production — it changes only what the requesting
 *    browser is shown, nothing server-side): `?hanro_geo=XX` on any page makes
 *    this browser session behave as a visitor from country XX, and clears any
 *    earlier dismissal so the visit starts as a first-time one. The value sticks
 *    for the rest of the tab session so later pages keep simulating XX.
 *    `?hanro_geo=clear` drops the simulation and the dismissal.
 *
 *    Dismissal: closing the overlay without choosing a country (close button,
 *    "stay" button, Escape or a tap on the backdrop) is remembered in
 *    localStorage for DISMISS_DAYS on that storefront's domain; the visitor is
 *    not asked again on later visits within that window, then asked once more.
 *
 *    Consent: the overlay waits until Shopify's cookie consent banner has been
 *    answered (or is not shown), and steps aside if the banner appears while it
 *    is open, so a modal dialog never renders the banner inert.
 *
 * 2. Header country selector — the six storefronts are appended to every
 *    Horizon localization form in the header group (desktop dropdown and mobile
 *    drawer), after the Shopify-market countries, which stay untouched. A
 *    MutationObserver re-appends them if a section render morphs the list.
 */
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';

const STOREFRONT_COUNTRIES = ['GB', 'DE', 'CH', 'FR', 'IT', 'ES'];
const OVERRIDE_PARAM = 'hanro_geo';
const OVERRIDE_KEY = 'hanro-geo-override';
const DETECTED_KEY = 'hanro-geo-detected';
const DISMISSED_KEY = 'hanro-country-overlay-dismissed';
const DISMISS_DAYS = 30;
const CONSENT_BANNER_ID = 'shopify-pc__banner';

/* ---------- storage helpers (Safari private mode throws) ---------- */

/** @param {Storage | undefined} store @param {string} key */
function read(store, key) {
  try {
    return store?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

/** @param {Storage | undefined} store @param {string} key @param {string | null} value */
function write(store, key, value) {
  try {
    if (value === null) store?.removeItem(key);
    else store?.setItem(key, value);
  } catch {
    /* storage unavailable */
  }
}

const session = (() => {
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
})();

const local = (() => {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
})();

/* ---------- market detection ---------- */

/** Applies the ?hanro_geo= test hook, if present. */
function applyOverrideParam() {
  const params = new URLSearchParams(window.location.search);
  if (!params.has(OVERRIDE_PARAM)) return;

  const value = (params.get(OVERRIDE_PARAM) ?? '').trim().toUpperCase();
  write(local, DISMISSED_KEY, null);

  if (value === '' || value === 'CLEAR') {
    write(session, OVERRIDE_KEY, null);
  } else if (/^[A-Z]{2}$/.test(value)) {
    write(session, OVERRIDE_KEY, value);
  }
}

/** @returns {Promise<string | null>} ISO country code of the visitor. */
async function resolveVisitorCountry() {
  const override = read(session, OVERRIDE_KEY);
  if (override) return override;

  const cached = read(session, DETECTED_KEY);
  if (cached) return cached;

  try {
    const root = window.Shopify?.routes?.root ?? '/';
    const response = await fetch(`${root}browsing_context_suggestions.json?country[enabled]=true`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) return null;

    const data = await response.json();
    const code = String(data?.detected_values?.country?.handle ?? '').toUpperCase();
    if (!/^[A-Z]{2}$/.test(code)) return null;

    write(session, DETECTED_KEY, code);
    return code;
  } catch {
    return null;
  }
}

function isDismissed() {
  const at = Number(read(local, DISMISSED_KEY));
  if (!at) return false;
  if (Date.now() - at < DISMISS_DAYS * 24 * 60 * 60 * 1000) return true;

  write(local, DISMISSED_KEY, null);
  return false;
}

/* ---------- Shopify consent banner ---------- */

function consentBannerVisible() {
  const banner = document.getElementById(CONSENT_BANNER_ID);
  if (!banner || banner.getClientRects().length === 0) return false;

  const style = window.getComputedStyle(banner);
  return style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity) !== 0;
}

/** @returns {Promise<boolean | null>} Whether Shopify intends to show its banner; null if unknown. */
function consentBannerExpected() {
  return new Promise((resolve) => {
    const shopify = /** @type {any} */ (window).Shopify;
    const ask = () => {
      try {
        const api = /** @type {any} */ (window).Shopify?.customerPrivacy;
        resolve(typeof api?.shouldShowBanner === 'function' ? Boolean(api.shouldShowBanner()) : null);
      } catch {
        resolve(null);
      }
    };

    if (shopify?.customerPrivacy) return ask();
    if (typeof shopify?.loadFeatures !== 'function') return resolve(null);

    const timeout = setTimeout(() => resolve(null), 3000);
    try {
      shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => {
        clearTimeout(timeout);
        ask();
      });
    } catch {
      clearTimeout(timeout);
      resolve(null);
    }
  });
}

/**
 * Resolves once the consent banner is out of the way: answered, closed, or
 * never shown. Waits a short grace period for a banner Shopify injects late.
 */
async function waitForConsentBanner() {
  const expected = await consentBannerExpected();
  if (expected === false && !consentBannerVisible()) return;

  const graceMs = expected ? 8000 : 2500;

  await new Promise((resolve) => {
    let seen = consentBannerVisible();
    let elapsed = 0;
    let done = false;

    const finish = () => {
      if (done) return;
      done = true;
      clearInterval(timer);
      document.removeEventListener('visitorConsentCollected', onConsent);
      resolve(undefined);
    };
    // Let the banner finish animating out before the overlay opens.
    const onConsent = () => setTimeout(finish, 400);

    document.addEventListener('visitorConsentCollected', onConsent);
    const timer = setInterval(() => {
      elapsed += 250;
      if (consentBannerVisible()) {
        seen = true;
      } else if (seen || elapsed >= graceMs) {
        finish();
      }
    }, 250);
  });
}

/* ---------- overlay ---------- */

class HanroCountryOverlay extends DialogComponent {
  #suspended = false;
  #bannerWatch = 0;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(DialogCloseEvent.eventName, this.#onClose);

    if (/** @type {any} */ (window).Shopify?.designMode) return;
    this.#start();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(DialogCloseEvent.eventName, this.#onClose);
    clearInterval(this.#bannerWatch);
  }

  async #start() {
    if (isDismissed()) return;

    const country = await resolveVisitorCountry();
    if (!country || !STOREFRONT_COUNTRIES.includes(country)) return;
    if (!this.isConnected) return;

    this.#prepare(country);
    await waitForConsentBanner();
    if (!this.isConnected || isDismissed()) return;

    this.#open();
  }

  /** @param {string} country */
  #prepare(country) {
    const options = this.querySelectorAll('.hanro-country-overlay__option');
    let countryName = country;

    for (const option of options) {
      if (!(option instanceof HTMLElement)) continue;
      const detected = option.dataset.countryCode === country;
      option.classList.toggle('button-secondary', !detected);
      option.closest('.hanro-country-overlay__item')?.toggleAttribute('data-detected', detected);
      if (detected) countryName = option.dataset.countryName ?? country;
    }

    const message = this.querySelector('.hanro-country-overlay__message');
    if (message) message.textContent = (this.dataset.message ?? '').replace('[country]', countryName);
  }

  #open() {
    this.#suspended = false;
    this.showDialog();

    // If Shopify's consent banner appears while the overlay is open, step
    // aside so the banner is not left inert behind a modal, then come back.
    clearInterval(this.#bannerWatch);
    this.#bannerWatch = window.setInterval(() => {
      if (!this.refs.dialog?.open || !consentBannerVisible()) return;
      clearInterval(this.#bannerWatch);
      this.#suspended = true;
      this.closeDialog();
      waitForConsentBanner().then(() => {
        if (this.isConnected && !isDismissed()) this.#open();
      });
    }, 500);
  }

  #onClose = () => {
    clearInterval(this.#bannerWatch);
    if (this.#suspended) return;
    write(local, DISMISSED_KEY, String(Date.now()));
  };
}

// Before the element is defined: defining it upgrades the rendered overlay
// synchronously, and its first dismissal check must see the test hook applied.
applyOverrideParam();

if (!customElements.get('hanro-country-overlay')) {
  customElements.define('hanro-country-overlay', HanroCountryOverlay);
}

/* ---------- header country selector ---------- */

/** @type {{ code: string, name: string, url: string }[]} */
const storefronts = (() => {
  try {
    return JSON.parse(document.getElementById('hanro-storefronts')?.textContent ?? '[]');
  } catch {
    return [];
  }
})();

/**
 * Builds a storefront entry from one of Horizon's own rendered list items, so
 * its markup, classes and styles match the existing countries exactly. The
 * Horizon click binding and market-specific attributes are stripped; the
 * `ref` is kept so Horizon's arrow-key navigation includes the entry.
 *
 * @param {Element} model
 * @param {{ code: string, name: string, url: string }} storefront
 * @param {number} index
 */
function buildItem(model, storefront, index) {
  const item = /** @type {HTMLElement} */ (model.cloneNode(true));

  for (const attribute of ['on:click', 'aria-current', 'aria-selected', 'data-aliases', 'hidden']) {
    item.removeAttribute(attribute);
  }
  item.id = `HanroStorefront-${storefront.code}-${index}`;
  item.dataset.value = storefront.code;
  item.dataset.hanroStorefront = storefront.code;
  item.dataset.href = storefront.url;
  item.setAttribute('aria-label', storefront.name);

  const name = item.querySelector('.country');
  if (name) name.textContent = storefront.name;
  item.querySelector('.localization-form__currency')?.remove();

  return item;
}

function injectHeaderStorefronts() {
  if (storefronts.length === 0) return;

  const lists = document.querySelectorAll('#header-group localization-form-component ul.countries');
  lists.forEach((list, index) => {
    if (list.querySelector('[data-hanro-storefront]')) return;

    const model = list.querySelector('li.localization-form__list-item');
    if (!model) return;

    list.append(...storefronts.map((storefront) => buildItem(model, storefront, index)));
  });
}

/** @param {EventTarget | null} target */
function storefrontItem(target) {
  return target instanceof Element ? target.closest('#header-group [data-hanro-storefront]') : null;
}

/** @param {Element} item */
function goToStorefront(item) {
  const href = item.getAttribute('data-href');
  if (href) window.location.assign(href);
}

document.addEventListener('click', (event) => {
  const item = storefrontItem(event.target);
  if (!item) return;
  event.preventDefault();
  goToStorefront(item);
});

// Capture phase: runs before Horizon's list keydown handler, whose Enter
// branch would otherwise submit the Shopify localization form with a country
// that is not a market on this store.
document.addEventListener(
  'keydown',
  (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const item = storefrontItem(event.target);
    if (!item) return;
    event.preventDefault();
    event.stopPropagation();
    goToStorefront(item);
  },
  { capture: true }
);

injectHeaderStorefronts();

const headerGroup = document.getElementById('header-group');
if (headerGroup) {
  let queued = false;
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      injectHeaderStorefronts();
    });
  }).observe(headerGroup, { childList: true, subtree: true });
}
